import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@clay-run/glaze/alert';
import { Button } from '@clay-run/glaze/button';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { useApi, useDraftState } from '../../../_shared/hooks';
import { FilterPopover } from '../../../_shared/components/flow';
import { getSegmentation, getSegmentationUsage } from '../../api-client';
import { CAMPAIGN_MEMBER_FILTER_FIELDS } from '../../campaign-member-filter-fields';
import { isVersionConflictError } from '../../conflict-errors';
import { wasLadderMoveAdopted } from '../../ladder-adoption-signal';
import { gateInPlayWrite, savePlaySegmentationConditions } from '../../ensure-play-owned';
import { setQualifyMembersOpen } from '../../members-panel-collapse-store';
import { saveSegmentationConditions } from '../../mutations';
import {
  libraryQueryKey,
  matchedMembersPrefixKey,
  segmentationDetailQueryKey,
  segmentationUsageQueryKey,
} from '../../query-keys';
import { clearRungScope, setRungScope, useRungScopeStore } from '../../rung-scope-store';
import { SAVE_GROUP_FIRST } from '../../sequence-card-menu';
import type { SegmentationConditions } from '../../schemas';
import { GROUP_MOVED_SENTENCE } from '../../usage-gate';
import { isUsageSidebarOpened, UsageSidebarOpenedError } from '../../usage-sidebar-errors';
import { openUsageSidebarFromUsage, type UsageSidebarOrigin } from '../../usage-sidebar-store';

/**
 * [R18][R20] The name a segmentation shows when its library row is not
 * loaded or no longer exists — NEVER the raw id. (A Library piece an admin
 * archived still has its row on the play-scoped read, U1 (b), so it shows
 * its real name plus an "Archived in Library" tag instead — see
 * `QualifyStep.tsx`'s `RungRow`/`PendingRungRow`.)
 */
export const SEGMENTATION_UNAVAILABLE_NAME = 'Segment unavailable';

/**
 * Launcher V2 PR C QA follow-ups, U4 — extracted from `QualifyStep`'s former
 * `EditConditionsDrawerBody` (PR C, U3C) so the SAME editor can be hosted
 * inline in more than one place: `QualifyStep`'s `RungRow`/`PendingRungRow`
 * (this unit, item 7) and `PlaybooksConfigPage`'s Library segmentation rows
 * (U5, item 21) — per the prototype's own explicit decision, "conditions are
 * edited in place on the waterfall, never in a sidebar"
 * (`playbooks-prototype-v6.html:8418`), and its Library-page mirror
 * (`libTgRow`, `:5182-5277`, "the same inline editor… pointed at the library
 * definition itself", `:5255`).
 *
 * Deliberately HOST-AGNOSTIC: no drawer/row/modal assumptions baked in here.
 * It takes a segmentation id, loads that segmentation's real conditions, edits
 * them via the same `FilterPopover`-based UI `AudienceRuleCard` uses, and
 * saves via `saveSegmentationConditions` — an IMMEDIATE-persist library-object
 * write, the same edit-model split `QualifyStep.tsx`'s header doc describes:
 * a rung only ever points at a segmentation id, so editing that segmentation's
 * conditions here never touches the playbook draft. `onSaved`/`onCancel` are
 * plain callbacks so each host decides what "done editing" means for its own
 * layout (collapse a row, close a section, etc.) without this component
 * knowing anything about hosts.
 */
export interface SegmentationConditionsEditorProps {
  segmentationId: string;
  /** Called after a successful save (conditions have been persisted). */
  onSaved?: () => void;
  /** Called when the rep dismisses the editor without saving. Omit to hide the Cancel button (e.g. a host with no other way to back out). */
  onCancel?: () => void;
  /**
   * Launcher V2 live-QA fixes, A5 — fired from a `useEffect` whenever the
   * draft's dirty-vs-loaded-head status changes, so a host (e.g.
   * `PlaybooksConfigPage`'s per-row Revert button) can gate its own
   * affordances without duplicating this editor's draft state.
   */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * U6 (R-Q1) — when provided, every `FilterPopover` Apply click ALSO stages a
   * live `'rung-draft'` preview onto the rung-scope store (see
   * `RungScope`'s doc comment): `MembersPanel` and this rung's own inline
   * "in bucket" count re-scope to the draft's live filter match, standalone
   * against the whole campaign, without saving. Omitted by
   * `PlaybooksConfigPage`'s Library editor (no campaign exists there to
   * preview against) and by `PendingRungRow` (an unsaved rung has no
   * `rung.id` to key a scope on).
   */
  previewScope?: { rungId: string; rungName: string; playbookId: string };
  /**
   * Playbooks QA round 4, U6 (S2) / Decision 13 — where a GATED save should
   * park its pending decision. When this segmentation turns out to be used in
   * more than one place (or by more than one unsent play), Save does NOT
   * write: it publishes the request to `usage-sidebar-store.ts` under this
   * origin and throws `UsageSidebarOpenedError`, and whichever host mounted
   * `useUsageSidebarDecision` for the SAME origin key executes the rep's
   * "Update all" / "Save a new version" choice later — the identical
   * fetch-open-throw shape `SequencesStep.tsx#saveWithGate` uses for
   * sequences.
   *
   * Optional and DEFENSIVE: a host that supplies no origin has nowhere to
   * park a decision (nothing would ever execute it, leaving the rep with a
   * sidebar whose buttons do nothing), so this component falls back to the
   * pre-U6 in-place write for that host rather than stranding the save. Every
   * host in the app supplies one today (`QualifyStep`'s two rung rows and
   * `PlaybooksConfigPage`'s library rows).
   */
  usageOrigin?: UsageSidebarOrigin;
  /**
   * Plan 2026-09-25-002, U7 (Decision 21) — passed from `QualifyStep` in play
   * mode so this save's `?playId=` lets the server's ownership guard confirm
   * the addressed segmentation belongs to this play. `undefined` in the
   * playbook doorway, which sends no `playId`.
   */
  playId?: string;
  /**
   * Plan 2026-09-27-006 U7b (Deferred to Implementation default) — the
   * addressed segmentation's OWN `owningPlayId` field, read by `QualifyStep`
   * off its already-fetched library summary and handed down as a prop rather
   * than this editor doing its own query-client lookup. `owningPlayId ===
   * playId` means this play already owns the identity (a copy, or a
   * play-created original) — {@link saveWithGate} writes it directly with no
   * usage fetch at all (Key Technical Decisions 3, 12). `undefined` outside a
   * play, where ownership never applies.
   */
  owningPlayId?: string | null;
  /**
   * Plan 2026-09-27-006 U7b — needed only when `playId` is set: the play's
   * own copy of a shared Library segmentation is forked via `ensurePlayOwned`
   * (Key Technical Decision 1), which threads the playbook draft/cache keys
   * through this id. Every play-mode host supplies it alongside `playId`.
   */
  playbookId?: string;
  /**
   * Plan 2026-09-28-006 U5 (rulebook R18) — `true` only for a Library piece
   * an admin archived that this play's ladder still references
   * (`isArchivedInLibrary`, read by the host off its own play-scoped library
   * row). ③ ("Update plays and the playbook") is hidden for it —
   * `saveMenuOptions` reads `target.archivedInLibrary`. `false`/omitted
   * everywhere else (outside a play, or a not-archived row).
   */
  archivedInLibrary?: boolean;
}

/**
 * Structural deep-equality for plain JSON-shaped values (this editor's
 * `SegmentationConditions` draft: nested filter groups/conditions arrays and
 * objects, no `Date`/`Map`/class instances) — used to decide whether the
 * draft actually differs from the loaded head, so Save doesn't render as
 * active on a pristine load. Deliberately local/minimal rather than a new
 * shared dependency; this repo has no existing deep-equal utility.
 */
function isDeepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => isDeepEqual(item, b[index]));
  }
  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const aKeys = Object.keys(aRecord);
  const bKeys = Object.keys(bRecord);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(
    (key) => Object.prototype.hasOwnProperty.call(bRecord, key) && isDeepEqual(aRecord[key], bRecord[key]),
  );
}

/**
 * Codex adversarial review of Playbooks QA round 6 — the STRUCTURE of a
 * conditions payload with every raw filter VALUE stripped, for the DEV-only
 * diagnostic log below.
 *
 * The log it feeds used to serialise the whole `SegmentationConditions`
 * object, and `FilterCondition.value` (`_shared/flow/filter.ts`) carries
 * whatever the rep typed or picked — `campaign-member-filter-fields.ts`'s
 * catalog includes **Contact Name** and **Contact Email**, so a dev/staging
 * console (and anything scraping it) was getting real contact PII. Vite
 * strips the `import.meta.env.DEV`-gated call in production, so prod was
 * never affected; dev and staging were.
 *
 * What survives here is exactly what the round-6 plan's H-A/H-B/H-C triage
 * needs and nothing more: field ids, operators, per-group and per-condition
 * COUNTS, the group logic, and a value KIND (`'null'`, `'array(0)'`,
 * `'string'`) — an empty/absent value is the whole H-A signature, and its
 * kind is enough to see it without its contents. `group.label` is
 * deliberately omitted too: it is free text a rep typed.
 */
function redactedConditionsShape(conditions: SegmentationConditions) {
  return {
    groupLogic: conditions.groupLogic,
    groupCount: conditions.groups.length,
    groups: conditions.groups.map((group) => ({
      logic: group.logic,
      conditionCount: group.conditions.length,
      conditions: group.conditions.map((condition) => ({
        field: condition.field,
        operator: condition.operator,
        valueKind: Array.isArray(condition.value)
          ? `array(${condition.value.length})`
          : condition.value === null
            ? 'null'
            : typeof condition.value,
      })),
    })),
  };
}

export function SegmentationConditionsEditor({
  segmentationId,
  onSaved,
  onCancel,
  onDirtyChange,
  previewScope,
  usageOrigin,
  playId,
  owningPlayId,
  playbookId,
  archivedInLibrary,
}: SegmentationConditionsEditorProps) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const query = useQuery({
    queryKey: segmentationDetailQueryKey(segmentationId),
    queryFn: () => getSegmentation(apiFetch, segmentationId),
  });

  /**
   * Playbooks QA round 6, U4 (item 1) — replaces the former bare `useState`
   * + reseed `useEffect` (a `terra/no-effect-reseeded-draft` violation the
   * lint rule's own gap in following one-hop local wrappers, like the old
   * `commitDraft`, missed — see this round's plan for the class audit). The
   * render-phase seed fires once per key, so a background refetch (new
   * `query.data` identity, same key) never re-seeds and discards an
   * in-progress edit.
   *
   * Plan 2026-09-29-001 U2 (rulebook R14; Codex round-4 B1) — the key is NOT
   * `segmentationId`. A group's segment id changes for two very different
   * reasons, and only one of them is the rep's doing:
   *
   *  - a REP-PICKED swap onto a different segment — reseed, as always: the
   *    filters of the segment they navigated away from are not this one's;
   *  - an ADOPTED move — copy-on-write forking this group's segment, another
   *    editor's copy arriving via the rep's own "Reload to see their change",
   *    a fan-out. The rep never chose any of those, and R14 says their typing
   *    survives them. Keying on the id meant Reload — the button whose whole
   *    job is "keep my work, show me theirs" — was itself what threw the
   *    unsaved filters away.
   *
   * `ladder-adoption-signal.ts` is the one thing that can tell them apart, so
   * the key only follows the id when the transition was NOT an adopted move.
   * `dirty` still compares against the NEW id's head, so after an adopted
   * move the editor shows the rep's filters, dirty, Save lit — which is
   * exactly right: they are unsaved against the piece the group now uses.
   *
   * The comparison happens in the render phase (React's documented "adjust
   * state during render" pattern — the same one `useDraftState` itself uses)
   * so the key is already correct on the render where the id prop changes;
   * an effect would run a render too late, after `useDraftState` had already
   * reseeded.
   */
  const [draftKey, setDraftKey] = useState(segmentationId);
  const seenSegmentationIdRef = useRef(segmentationId);
  if (seenSegmentationIdRef.current !== segmentationId) {
    const previousId = seenSegmentationIdRef.current;
    seenSegmentationIdRef.current = segmentationId;
    if (!wasLadderMoveAdopted('segmentation', previousId, segmentationId)) setDraftKey(segmentationId);
  }
  const form = useDraftState<SegmentationConditions>(query.data?.head?.conditions, draftKey);
  const draft = form.draft ?? null;

  /**
   * F1 fix — mirrors `draft` for SYNCHRONOUS reads within the same tick.
   * `FilterPopover`'s Apply (`useFilterDraft.apply`) can call
   * `onFiltersChange` then `onGroupLogicChange` back-to-back before this
   * component re-renders; reading `draft` (the render-time value) from the
   * second call would see the value from BEFORE the first call, clobbering
   * it (the lost-update bug this ref exists to prevent — see
   * `applyDraftChange` below). Kept alongside `useDraftState` rather than
   * relying on its own internal merge timing, because `setRungScope` (in
   * `applyDraftChange`) needs the FULLY merged draft synchronously, not
   * merely a correctly-merged `form.draft` on the NEXT render. Re-synced
   * from `draft` at the top of every render (a no-op once steady-state,
   * since `applyDraftChange` always writes the identical merged value
   * through both this ref and `form.setDraft`); only a genuine external
   * reseed — the initial load, or `form.reseedIfUnchanged` after a sole-use
   * save —
   * moves it from outside `applyDraftChange`.
   */
  const draftRef = useRef<SegmentationConditions | null>(draft);
  draftRef.current = draft;

  // A5 — "dirty" means the draft has diverged from the loaded head, not just
  // "a draft exists" (it always does, once seeded). Pristine-on-load must
  // read as NOT dirty so Save doesn't render active with nothing to save.
  const dirty = useMemo(
    () => query.data?.head != null && draft !== null && !isDeepEqual(draft, query.data.head.conditions),
    [draft, query.data],
  );

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  /**
   * U6 (R-Q1) — clears the rung-scope store's `rung-draft` preview, but ONLY
   * when it is still THIS editor's own scope: reads the store fresh via
   * `getState()` rather than closing over the `scope` this component might
   * have read on some earlier render (Codex challenge — stale-closure risk),
   * so Save/Cancel/unmount from an editor whose preview has already been
   * superseded by a DIFFERENT rung's own Apply click can never clear that
   * other rung's draft.
   */
  function clearOwnPreviewScope() {
    if (!previewScope) return;
    const currentScope = useRungScopeStore.getState().scope;
    if (currentScope?.kind === 'rung-draft' && currentScope.rungId === previewScope.rungId) {
      clearRungScope();
    }
  }

  // U6 (R-Q1) — unmount cleanup: a row collapsing (or the whole step
  // unmounting) without an explicit Cancel/Save must not leave a stale
  // preview scoped to a rung whose editor no longer exists. Dependency is
  // the rung id alone (not the whole `previewScope` object, which
  // `QualifyStep`/`PlaybooksConfigPage` construct fresh every render) so this
  // effect doesn't tear down and re-run on every unrelated parent re-render —
  // only when the editor is actually retargeted at a different rung.
  // `clearOwnPreviewScope` reads the store fresh via `getState()` and only
  // compares `previewScopeRungId` (not the whole `previewScope` object), so
  // `react-hooks/exhaustive-deps` has nothing to flag on this effect — an
  // `eslint-disable` here would itself be an unused-directive lint error
  // (F7).
  const previewScopeRungId = previewScope?.rungId;
  useEffect(() => {
    return () => {
      clearOwnPreviewScope();
    };
  }, [previewScopeRungId]);

  /**
   * Stages a draft edit AND, when this row is being previewed live, the
   * rung-scope store's draft preview (U6, R-Q1) — see `previewScope`'s doc
   * comment.
   *
   * F1 fix — takes a PATCH (just the field(s) that changed) rather than a
   * whole next-draft object, and reads the base to merge onto from
   * `draftRef.current` rather than the `draft` state variable. `FilterPopover`
   * can fire `onFiltersChange` then `onGroupLogicChange` synchronously in the
   * same tick (`useFilterDraft.apply`, when Apply changes both the filters
   * AND the group logic) — both calls happen before this component
   * re-renders, so a version that read the `draft` closure would have the
   * second call overwrite the first call's write with a stale copy of
   * everything else. Reading `draftRef.current` (updated synchronously
   * inside the first call, ahead of the `form.setDraft` that eventually
   * lands on the NEXT render) means the second call composes onto what the
   * first call just wrote instead of clobbering it.
   */
  function applyDraftChange(patch: Partial<Pick<SegmentationConditions, 'groups' | 'groupLogic'>>) {
    const base = draftRef.current;
    if (base === null) return;
    const next = { ...base, ...patch };
    draftRef.current = next;
    form.setDraft(next);
    if (previewScope) {
      setRungScope({ kind: 'rung-draft', ...previewScope, groups: next.groups, groupLogic: next.groupLogic });
      // Same gesture the Preview button itself performs (`QualifyStep.tsx`'s
      // `onPreview`) — Apply behaves like Preview so the rep doesn't have to
      // separately open the panel to see what they just staged.
      setQualifyMembersOpen(true);
    }
  }

  /**
   * Plan 2026-09-28-006 U5 (rulebook R5) — the SAVE GATE, mirroring
   * `SequencesStep.tsx#savePlayWithGate` for segmentations. Every save fetches
   * usage FRESH (`staleTime: 0`) FIRST, whoever owns the segmentation — the
   * pre-U5 shortcut that skipped the fetch entirely for an owned identity is
   * gone (the R5 defect: a copy shared by two groups in the same play must
   * still open the Save menu, not write straight through because this play
   * happens to own it).
   *
   * From a RUNG origin (`'play'`/`'playbook'`), `usageGateNeedsConfirmation(places)`
   * false — a segmentation used in zero or one place, whose one place's
   * playbook holds at most one unsent play — is exactly the silent write,
   * with no dialog at all: this must never become confirm-every-save (the
   * same rule round-3-QA-fixes wrote for the sequence gate). From a LIBRARY
   * origin, R5-6/R5-7 (playbooks-qa-round5-feedback.md) switches to
   * `libraryUsageGateNeedsConfirmation`, which fires at `places.length === 1`
   * too — see that function's own doc for why the Library doorway has no
   * self-place to discount. Above whichever threshold applies, this publishes
   * the pending request to the usage sidebar and throws
   * {@link UsageSidebarOpenedError}, which `handleSave` swallows so no error
   * toast fires and the editor KEEPS its draft (the rep's conditions are also
   * carried on the pending request itself, so the decision can still execute
   * even if this editor unmounts first).
   *
   * Returns the id actually written (`false` when gated/not written — the
   * usage sidebar now owns the decision), so `handleSave` knows both whether
   * to run its invalidations/toast/`onSaved` AND — plan 2026-09-28-003 — which
   * id to invalidate: on the play fork path that id is the newly-owned COPY,
   * never the Library id `segmentationId` was captured as at call time (the
   * `:487` closure bug this plan fixes: invalidating the Library id left the
   * copy's own detail cache holding the Library's stale conditions, so the
   * editor showed no error, `dirty === false`, and the rep's edit looked
   * silently lost even though the server had it).
   */
  async function saveWithGate(
    conditions: SegmentationConditions,
    headVersionId: string,
  ): Promise<string | false> {
    const usage = await queryClient.fetchQuery({
      queryKey: segmentationUsageQueryKey(segmentationId),
      queryFn: () => getSegmentationUsage(apiFetch, segmentationId),
      staleTime: 0,
    });
    const { places } = usage;
    // [R5] An edit to a segmentation THIS PLAY already owns (a copy, or a
    // play-created original) is gated by the SAME fresh usage fetch as a
    // Library piece — only the WRITE differs once ungated (see below).
    const owned = playId !== undefined && owningPlayId === playId;
    // Plan 2026-09-28-008 U2 (R14) — the Save panel's input, built once: the
    // menu, a stale refusal and a silent write's 409 all open it (the last
    // two already stale, holding the rep's conditions — the editor keeps
    // them too; nothing here reloads or reseeds). `null` without an origin
    // to park a decision under.
    const openSavePanel =
      usageOrigin === undefined
        ? null
        : (staleRefusal?: string): never => {
            openUsageSidebarFromUsage(
              usage,
              usageOrigin,
              {
                kind: 'segmentation',
                id: segmentationId,
                name: query.data?.segmentation.name ?? SEGMENTATION_UNAVAILABLE_NAME,
                owningPlayId: owningPlayId ?? null,
                archivedInLibrary: archivedInLibrary ?? false,
              },
              // A segmentation save carries conditions, never steps.
              {
                draftSteps: null,
                draftConditions: conditions,
                headVersionId,
                ...(staleRefusal === undefined ? {} : { staleRefusal }),
              },
            );
            throw new UsageSidebarOpenedError();
          };
    // {@link gateInPlayWrite} (`ensure-play-owned.ts`) is the ONE client
    // gate every in-play/in-playbook save goes through — see its own doc for
    // the library/play/playbook-doorway thresholds. No origin to park a
    // decision under → nothing would ever execute the rep's choice, so fall
    // back to the pre-U6 write rather than opening a dead sidebar (see
    // `usageOrigin`'s own doc).
    if (usageOrigin !== undefined && openSavePanel !== null) {
      const gate = gateInPlayWrite({ places, origin: usageOrigin });
      if (gate === 'menu') return openSavePanel();
      if (gate === 'stale') return openSavePanel(GROUP_MOVED_SENTENCE);
    }
    try {
      // Plan 2026-09-29-001 U1 (rulebook R3/R5/R14) — every in-play write
      // names its group; hoisted above BOTH branches below (the owned
      // silent write and the fork-first Library write) so a rung staged
      // this session (no server id yet) is refused the SAME way regardless
      // of which branch would otherwise have run. [R20] Every play-mode
      // host supplies a `'play'`/`'playbook'` origin (never `'library'`,
      // which has no `playId`) — see `usageOrigin`'s own doc.
      const originRungId = playId === undefined ? null : usageOrigin !== undefined && usageOrigin.kind !== 'library' ? usageOrigin.rungId : null;
      if (playId !== undefined && originRungId === null) {
        addErrorToast({ content: SAVE_GROUP_FIRST });
        return false;
      }
      // [R5] Owned & ungated — direct write on the play's own identity,
      // whatever kind of origin this is (rung or doorway). No fork: the
      // identity is already this play's own.
      if (owned) {
        await saveSegmentationConditions(apiFetch, segmentationId, conditions, headVersionId, 1, {
          playId,
          // A silent write is sole-use by construction (the gate above
          // already refused otherwise) — the group set it was checked
          // against is exactly the one group making it.
          group: { rungId: originRungId!, confirmedRungIds: [originRungId!] },
        });
        return segmentationId;
      }
      // Plan 2026-09-27-006 U7b (Key Technical Decision 12) — inside a play,
      // a sole-use (or ungated) Library segmentation copies first, FOR THIS
      // GROUP ONLY (rulebook R3 — `rungId` names it). Plan 2026-09-28-003 —
      // `savePlaySegmentationConditions` is the ONE place that ordering
      // happens: the fork is adopted (the rung repointed onto the copy) only
      // AFTER the copy carries these conditions and the client already holds
      // that written head in the segmentation-detail cache — otherwise the
      // still-mounted editor retargets onto the copy and reseeds its draft
      // from the copy's stale PRE-EDIT (Library) head before this write had
      // even landed (this plan's whole bug). The write lands on the copy's
      // head, never the Library head.
      if (playId !== undefined) {
        const saved = await savePlaySegmentationConditions(apiFetch, queryClient, {
          playId,
          // Every play-mode host supplies `playbookId` alongside `playId` —
          // see that prop's own doc comment.
          playbookId: playbookId!,
          segmentationId,
          rungId: originRungId!,
          conditions,
          expectedVersionId: headVersionId,
          confirmedPlaceCount: 1,
          confirmedRungIds: [originRungId!],
        });
        return saved.ownedId;
      }
      // Playbooks QA round 6, U2 (item 1) — the reported "conditions lost
      // after Preview → Apply filters → Save" was NOT reproduced by the
      // user's own retry, by code reading, or by a jsdom probe (see this
      // round's plan). This DEV-only, self-diagnosing fallback logs exactly
      // what H-A/H-B/H-C would need to distinguish a future recurrence. No
      // new UI; never runs in production (`import.meta.env.DEV`).
      //
      // Codex adversarial review of round 6 — TWO fixes here. (a) The body
      // is summarised through `redactedConditionsShape` (see its doc): the
      // former version logged raw `FilterCondition.value`s, i.e. real
      // contact PII, into dev/staging consoles. (b) BOTH outcomes are
      // logged now. The success-only version left a REJECTED save with no
      // diagnostic at all — and since this log exists precisely as the
      // fallback for a bug that was never reproduced, the error case is the
      // more valuable half of it (it is what distinguishes H-B from H-A/H-C
      // in one line).
      const logged = {
        segmentationId,
        conditions: redactedConditionsShape(conditions),
        expectedVersionId: headVersionId,
        confirmedPlaceCount: places.length,
      };
      let response: Awaited<ReturnType<typeof saveSegmentationConditions>>;
      try {
        response = await saveSegmentationConditions(
          apiFetch,
          segmentationId,
          conditions,
          headVersionId,
          places.length,
          { playId },
        );
      } catch (error) {
        if (import.meta.env.DEV) {
          console.debug('[SegmentationConditionsEditor] conditions save FAILED', {
            ...logged,
            // The server's own error sentence (the same text `handleSave`
            // toasts) — never the payload it rejected.
            error: error instanceof Error ? `${error.name}: ${error.message}` : 'non-Error rejection',
          });
        }
        throw error;
      }
      if (import.meta.env.DEV) {
        console.debug('[SegmentationConditionsEditor] conditions saved', {
          ...logged,
          responseVersionId: response.versionId,
        });
      }
      return segmentationId;
    } catch (error) {
      // [R14] refused as stale — the Save panel holds the rep's conditions
      // (and the editor still shows them); only their Reload refetches.
      if (openSavePanel !== null && isVersionConflictError(error)) {
        return openSavePanel(error instanceof Error ? error.message : GROUP_MOVED_SENTENCE);
      }
      throw error;
    }
  }

  async function handleSave() {
    if (!query.data?.head || draft === null) return;
    setSaving(true);
    // Codex adversarial review of Playbooks QA round 6 (BLOCKING finding 1) —
    // the draft AS DISPATCHED. Everything below runs after at least one
    // `await`, and the `FilterPopover` above stays fully interactive for that
    // whole window (only Save itself is disabled while `saving`), so by the
    // time the response lands `form.draft` may legitimately have moved on to
    // a newer Apply. Naming the snapshot here is what lets the post-save
    // reseed tell "nothing changed since I dispatched" from "the rep kept
    // working".
    const dispatched = draft;
    try {
      const wroteId = await saveWithGate(dispatched, query.data.head.versionId);
      if (!wroteId) return;
      // Plan 2026-09-28-003 — invalidates the id actually WRITTEN, not the
      // `segmentationId` this closure captured at render time: on the play
      // fork path those differ (the copy vs. the Library id), and after
      // `savePlaySegmentationConditions` the copy's cache is already fresh,
      // so this is belt-and-braces there — but the Library-id invalidation
      // it replaces was simply wrong (the `:487` closure bug this plan
      // fixes), leaving the copy's detail cache pointed at nothing.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: segmentationDetailQueryKey(wroteId) }),
        queryClient.invalidateQueries({ queryKey: libraryQueryKey() }),
        queryClient.invalidateQueries({ queryKey: matchedMembersPrefixKey() }),
      ]);
      // AFTER the invalidations above resolve (they await the active ladder
      // query's refetch) — clearing the preview before that would flip the
      // row from the live preview count straight to a STALE real ladder
      // count for one tick, flashing the pre-save number.
      clearOwnPreviewScope();
      // Playbooks QA round 6, U4 (item 1) — the post-save reseed
      // `docs/v4-system/draft-state-under-sse.md`'s canonical pattern calls
      // for: adopts the just-saved value as BOTH `draft` and `pristine`, so
      // `form.isDirty` (unused by this editor's own `dirty` today, but kept
      // correct for any future reader) clears the instant the sole-use
      // write lands, without waiting on the invalidation's refetch to
      // change `query.data` identity.
      //
      // Codex adversarial review of round 6 (BLOCKING finding 1) —
      // `reseedIfUnchanged`, NOT `reseed`. The unconditional version wrote
      // the `dispatched` snapshot back over `form.draft` when the request
      // resolved, so an Apply the rep made while the save was in flight was
      // silently discarded AND the editor then re-read as clean/persisted
      // (Save disabled) — their work gone with no error. This is exactly the
      // race `useDraftState.ts`'s own doc describes for `reseedIfUnchanged`
      // ("keystrokes typed between `mutate()` and the server response"),
      // reached through a mouse gesture instead of a keystroke, so the fix
      // is to use the primitive that already exists for it rather than a
      // bespoke guard. It errs the safe way: any doubt about whether the
      // draft moved skips the reseed, which only leaves `pristine` behind
      // (the editor's own `dirty` is computed against `query.data.head`, not
      // `form.isDirty`) instead of destroying a rep's edit. It also fixes a
      // second, quieter case for free: a save that resolves after the row
      // has been retargeted at a DIFFERENT segmentation no longer stamps
      // this segmentation's conditions onto that one's draft.
      form.reseedIfUnchanged(dispatched, dispatched);
      addToast({ content: 'Conditions saved.', status: 'success' });
      onSaved?.();
    } catch (error) {
      // U6 (S2) — the gate's sentinel is not a failure: the sidebar is now
      // open with this exact request pending, so there is nothing to toast.
      // Deliberately does NOT call `onSaved`/`onCancel` either, so the editor
      // keeps its draft visible behind the sidebar and the rep can choose
      // "Back to editing".
      if (isUsageSidebarOpened(error)) return;
      // Deliberately does NOT call `onSaved`/`onCancel` on failure — the host
      // must not collapse/close the editor out from under an error the rep
      // still needs to see and act on.
      addErrorToast({
        content: error instanceof Error ? error.message : 'Could not save these conditions — try again.',
      });
    } finally {
      setSaving(false);
    }
  }

  if (query.isLoading) {
    return (
      <div className="flex justify-center p-6">
        <LoadingSpinner size="md" />
      </div>
    );
  }
  if (query.isError || !query.data?.head || draft === null) {
    return <Alert status="error">Couldn&apos;t load this segment&apos;s conditions.</Alert>;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Same light-mode/dark-mode `--color-content-action` remap as
          `AudienceRuleCard`'s embed — see that component's doc comment for
          why `FilterPopover`'s own hardcoded action-button classes need it. */}
      <div className="rounded-md border border-border-primary bg-bg-primary p-3 [--color-content-action:var(--color-bg-action-primary)] [--color-content-action-hover:var(--color-bg-action-primary-hover)]">
        <FilterPopover
          filter={draft.groups}
          onFiltersChange={(groups) => applyDraftChange({ groups })}
          groupLogic={draft.groupLogic}
          onGroupLogicChange={(groupLogic) => applyDraftChange({ groupLogic })}
          fields={CAMPAIGN_MEMBER_FILTER_FIELDS}
        />
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button
            variant="secondary"
            onClick={() => {
              clearOwnPreviewScope();
              onCancel();
            }}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          loading={saving}
          disabled={!dirty || saving}
          onClick={() => void handleSave()}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
